import { parseISO } from 'date-fns';
import type { DelegateCalendarEventRow } from './executiveCalendarDelegatedService';
import type { PlannedSegment } from './calendarEventGroupPlan';

export type ConflictResolutionAction = 'adjust' | 'delete' | 'cutout';

const MIN_CUTOUT_MS = 60_000;

/** One schedulable conflict — a single event or a linked trip group. */
export type TripConflictUnit = {
  key: string;
  kind: 'event' | 'group';
  title: string;
  events: DelegateCalendarEventRow[];
  start: Date;
  end: Date;
  durationMs: number;
  overlapStart: Date;
  overlapEnd: Date;
  groupId?: string;
  isGrouped: boolean;
  canModify: boolean;
};

export type ConflictDeleteOp = {
  kind: 'delete';
  unit: TripConflictUnit;
};

export type ConflictAdjustOp = {
  kind: 'adjust';
  unit: TripConflictUnit;
  start: Date;
  end: Date;
};

export type ConflictCutoutOp = {
  kind: 'cutout';
  unit: TripConflictUnit;
  /** Remaining pieces after punching travel-time holes. */
  ranges: Array<{ start: Date; end: Date }>;
};

export type ConflictResolutionPlan = {
  deletes: ConflictDeleteOp[];
  adjusts: ConflictAdjustOp[];
  cutouts: ConflictCutoutOp[];
};

function parseEventRange(ev: DelegateCalendarEventRow): { start: Date; end: Date } | null {
  if (!ev.start || !ev.end || ev.isAllDay) return null;
  try {
    const start = parseISO(ev.start);
    const end = parseISO(ev.end);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end <= start) return null;
    return { start, end };
  } catch {
    return null;
  }
}

export function intervalsOverlap(
  aStart: Date,
  aEnd: Date,
  bStart: Date,
  bEnd: Date,
): boolean {
  return aStart < bEnd && bStart < aEnd;
}

/** Combined time span of all scheduled trip segments. */
export function getTripBounds(
  segments: PlannedSegment[],
): { start: Date; end: Date } | null {
  const scheduled = segments.filter(s => s.start && s.end);
  if (scheduled.length === 0) return null;

  let start = scheduled[0].start!;
  let end = scheduled[0].end!;
  for (const seg of scheduled) {
    if (seg.start! < start) start = seg.start!;
    if (seg.end! > end) end = seg.end!;
  }
  return { start, end };
}

function eventUnitKey(ev: DelegateCalendarEventRow): string {
  return `event:${ev.calendarId}:${ev.id}`;
}

function groupUnitKey(groupId: string): string {
  return `group:${groupId}`;
}

function unitTitle(events: DelegateCalendarEventRow[], kind: 'event' | 'group'): string {
  if (kind === 'group') {
    const primary = events.find(e => !/drive:/i.test(e.title || '')) || events[0];
    const label = primary?.title || 'Linked trip';
    return events.length > 1 ? `${label} (${events.length} linked events)` : label;
  }
  return events[0]?.title || '(No title)';
}

function canModifyUnit(
  events: DelegateCalendarEventRow[],
  writable: Set<string>,
): boolean {
  if (writable.size === 0) return true;
  return events.every(ev => writable.has(ev.calendarId));
}

/** Collapse overlapping events into resolution units (standalone or linked trip). */
export function findTripConflictUnits(
  segments: PlannedSegment[],
  existingEvents: DelegateCalendarEventRow[],
  options?: {
    writableCalendarIds?: string[];
    getGroupId?: (ev: DelegateCalendarEventRow) => string | undefined;
  },
): TripConflictUnit[] {
  const scheduled = segments.filter(s => s.start && s.end);
  if (scheduled.length === 0) return [];

  const tripBounds = getTripBounds(segments);
  if (!tripBounds) return [];

  const writable = new Set(options?.writableCalendarIds || []);
  const getGroupId =
    options?.getGroupId ||
    ((ev: DelegateCalendarEventRow) => ev.extendedPropertiesPrivate?.lctfGroupId);

  const overlapping: Array<{
    ev: DelegateCalendarEventRow;
    range: { start: Date; end: Date };
    overlapStart: Date;
    overlapEnd: Date;
    groupId?: string;
  }> = [];

  for (const ev of existingEvents) {
    const range = parseEventRange(ev);
    if (!range) continue;
    if (!intervalsOverlap(tripBounds.start, tripBounds.end, range.start, range.end)) continue;

    overlapping.push({
      ev,
      range,
      overlapStart: range.start > tripBounds.start ? range.start : tripBounds.start,
      overlapEnd: range.end < tripBounds.end ? range.end : tripBounds.end,
      groupId: getGroupId(ev),
    });
  }

  if (overlapping.length === 0) return [];

  const unitMap = new Map<string, TripConflictUnit>();

  for (const row of overlapping) {
    const key = row.groupId ? groupUnitKey(row.groupId) : eventUnitKey(row.ev);
    const existing = unitMap.get(key);

    if (!existing) {
      unitMap.set(key, {
        key,
        kind: row.groupId ? 'group' : 'event',
        title: unitTitle([row.ev], row.groupId ? 'group' : 'event'),
        events: [row.ev],
        start: row.range.start,
        end: row.range.end,
        durationMs: row.range.end.getTime() - row.range.start.getTime(),
        overlapStart: row.overlapStart,
        overlapEnd: row.overlapEnd,
        groupId: row.groupId,
        isGrouped: Boolean(row.groupId),
        canModify: canModifyUnit([row.ev], writable),
      });
      continue;
    }

    existing.events.push(row.ev);
    if (row.range.start < existing.start) existing.start = row.range.start;
    if (row.range.end > existing.end) existing.end = row.range.end;
    existing.durationMs = existing.end.getTime() - existing.start.getTime();
    if (row.overlapStart < existing.overlapStart) existing.overlapStart = row.overlapStart;
    if (row.overlapEnd > existing.overlapEnd) existing.overlapEnd = row.overlapEnd;
    existing.title = unitTitle(existing.events, existing.kind);
    existing.canModify = canModifyUnit(existing.events, writable);
  }

  return Array.from(unitMap.values()).sort((a, b) => a.start.getTime() - b.start.getTime());
}

export function defaultConflictActions(
  units: TripConflictUnit[],
  travelSegments: PlannedSegment[] = [],
): Record<string, ConflictResolutionAction> {
  const actions: Record<string, ConflictResolutionAction> = {};
  for (const unit of units) {
    actions[unit.key] = canCutout(unit, travelSegments) ? 'cutout' : 'adjust';
  }
  return actions;
}

function travelRangesFromSegments(segments: PlannedSegment[]): Array<{ start: Date; end: Date }> {
  return segments
    .filter(s => s.type === 'travel' && s.start && s.end)
    .map(s => ({ start: s.start!, end: s.end! }))
    .sort((a, b) => a.start.getTime() - b.start.getTime());
}

function subtractInterval(
  ranges: Array<{ start: Date; end: Date }>,
  holeStart: Date,
  holeEnd: Date,
): Array<{ start: Date; end: Date }> {
  const out: Array<{ start: Date; end: Date }> = [];
  for (const range of ranges) {
    if (holeEnd <= range.start || holeStart >= range.end) {
      out.push(range);
      continue;
    }
    if (holeStart > range.start) {
      out.push({ start: range.start, end: holeStart });
    }
    if (holeEnd < range.end) {
      out.push({ start: holeEnd, end: range.end });
    }
  }
  return out.filter(r => r.end.getTime() - r.start.getTime() >= MIN_CUTOUT_MS);
}

/** Remove travel-time windows from an existing event, leaving one or more free pieces. */
export function computeCutoutRanges(
  evStart: Date,
  evEnd: Date,
  travelSegments: PlannedSegment[],
): Array<{ start: Date; end: Date }> {
  let ranges = [{ start: evStart, end: evEnd }];
  for (const travel of travelRangesFromSegments(travelSegments)) {
    ranges = subtractInterval(ranges, travel.start, travel.end);
  }
  return ranges;
}

/** Cut out is only for standalone events partially overlapped by travel legs. */
export function canCutout(unit: TripConflictUnit, travelSegments: PlannedSegment[]): boolean {
  if (unit.kind !== 'event' || unit.events.length !== 1 || !unit.canModify) return false;
  const range = parseEventRange(unit.events[0]);
  if (!range) return false;
  const cut = computeCutoutRanges(range.start, range.end, travelSegments);
  if (cut.length === 0) return false;
  const originalMs = range.end.getTime() - range.start.getTime();
  const cutMs = cut.reduce((sum, r) => sum + (r.end.getTime() - r.start.getTime()), 0);
  return cutMs < originalMs;
}

export function formatCutoutPreview(ranges: Array<{ start: Date; end: Date }>): string {
  if (ranges.length === 0) return 'Remove event';
  return ranges
    .map(r => `${formatTime(r.start)} – ${formatTime(r.end)}`)
    .join(' + ');
}

function formatTime(d: Date): string {
  return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

/** Build delete + adjust + cutout ops with times that account for which units are removed. */
export function buildConflictResolutionPlan(
  units: TripConflictUnit[],
  actions: Record<string, ConflictResolutionAction>,
  tripEnd: Date,
  travelSegments: PlannedSegment[] = [],
): ConflictResolutionPlan {
  const modifiable = units.filter(u => u.canModify);
  const deleteUnits = modifiable.filter(u => actions[u.key] === 'delete');
  const deletedGroupIds = new Set(
    deleteUnits.map(u => u.groupId).filter((id): id is string => Boolean(id)),
  );
  const deletedEventKeys = new Set(
    deleteUnits.filter(u => !u.groupId).map(u => u.key),
  );

  const deletes: ConflictDeleteOp[] = [];
  const seenDeleteKeys = new Set<string>();
  for (const unit of deleteUnits) {
    if (seenDeleteKeys.has(unit.key)) continue;
    seenDeleteKeys.add(unit.key);
    deletes.push({ kind: 'delete', unit });
  }

  const adjustUnits = modifiable
    .filter(u => actions[u.key] === 'adjust')
    .filter(u => {
      if (u.groupId && deletedGroupIds.has(u.groupId)) return false;
      if (!u.groupId && deletedEventKeys.has(u.key)) return false;
      return true;
    })
    .sort((a, b) => a.start.getTime() - b.start.getTime());

  const cutoutUnits = modifiable
    .filter(u => actions[u.key] === 'cutout')
    .filter(u => {
      if (u.groupId && deletedGroupIds.has(u.groupId)) return false;
      if (!u.groupId && deletedEventKeys.has(u.key)) return false;
      return true;
    });

  let cursorMs = tripEnd.getTime();
  const adjusts: ConflictAdjustOp[] = adjustUnits.map(unit => {
    const start = new Date(cursorMs);
    const end = new Date(cursorMs + unit.durationMs);
    cursorMs = end.getTime();
    return { kind: 'adjust', unit, start, end };
  });

  const cutouts: ConflictCutoutOp[] = [];
  for (const unit of cutoutUnits) {
    const ev = unit.events[0];
    const range = parseEventRange(ev);
    if (!range) continue;
    const ranges = computeCutoutRanges(range.start, range.end, travelSegments);
    if (ranges.length === 0) {
      deletes.push({ kind: 'delete', unit });
      continue;
    }
    cutouts.push({ kind: 'cutout', unit, ranges });
  }

  return { deletes, adjusts, cutouts };
}

export function conflictKey(unit: TripConflictUnit): string {
  return unit.key;
}
